import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PostThumb from "@/components/PostThumb";
import Seo from "@/components/Seo";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("All");

  useEffect(() => {
    api
      .get("/posts")
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(posts.map((p) => p.category));
    return ["All", ...Array.from(set)];
  }, [posts]);

  const visible = active === "All" ? posts : posts.filter((p) => p.category === active);
  const [featured, ...rest] = visible;

  return (
    <main data-testid="blog-page" className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <Seo
        title="Journal"
        description="Long-form essays on money, decision-making, and the mindset behind them. From the Moneyssance journal."
        path="/blog"
      />
      <div className="mb-14 md:mb-20">
        <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-4">
          The Journal
        </div>
        <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tighter leading-[0.95]">
          Words on money,
          <br />
          <span className="italic text-primary">written slowly.</span>
        </h1>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-14" data-testid="blog-categories">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setActive(c)}
            data-testid={`blog-category-${c.toLowerCase()}`}
            className={`text-xs uppercase tracking-widest px-4 py-2 rounded-full border transition-colors duration-200 ${
              active === c
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-foreground/70 hover:border-primary hover:text-primary"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading && (
        <div data-testid="blog-loading" className="text-foreground/60">
          Loading…
        </div>
      )}

      {!loading && visible.length === 0 && (
        <div data-testid="blog-empty" className="text-foreground/60 py-20 border border-dashed border-border rounded-sm text-center">
          No posts in this category yet.
        </div>
      )}

      {/* Featured */}
      {featured && (
        <Link
          to={`/blog/${featured.slug}`}
          data-testid={`blog-featured-${featured.slug}`}
          className="group block mb-20"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <PostThumb post={featured} className="aspect-[16/10]" />
            </div>
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-widest text-accent">
                Featured · {featured.category} · {featured.read_minutes} min
              </div>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight tracking-tighter group-hover:text-primary transition-colors">
                {featured.title}
              </h2>
              <p className="mt-6 text-lg text-foreground/70 leading-relaxed">
                {featured.excerpt}
              </p>
              <div className="mt-8 text-sm uppercase tracking-widest text-primary">
                Read the piece →
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* Rest */}
      {rest.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 border-t border-border pt-16">
          {rest.map((p) => (
            <Link
              key={p.post_id}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group block"
            >
              <PostThumb post={p} className="aspect-[4/3]" />
              <div className="mt-5 text-[10px] uppercase tracking-widest text-accent">
                {p.category} · {p.read_minutes} min
              </div>
              <h3 className="mt-2 font-serif text-2xl leading-tight tracking-tight group-hover:text-primary transition-colors">
                {p.title}
              </h3>
              <p className="mt-3 text-sm text-foreground/70 leading-relaxed line-clamp-3">
                {p.excerpt}
              </p>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}
